/**
 * Solid hooks for multi-currency subsystem:
 *
 *   - `useExchangeRatesQuery`: global rate catalog (`exchange_rate` table,
 *     USD pivot) with canonical queryKey `queryKeys.exchangeRates.all`.
 *   - `useFinanceSettings`: reactive user financial settings
 *     (`base_currency`, `month_start_day`) read from local `user` row.
 *     Replaces hardcoded `TODO(F2)` (MONTH_START_DAY = 1, base currency
 *     inferred from first wallet) in tx-list-view / dashboard.
 *
 * Same pattern as `use-finance-query.ts`: consumers never call
 * `createPowerSyncQuery` directly for this data.
 */

import { type Accessor, createMemo } from "solid-js";
import { eq } from "drizzle-orm";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { type PowerSyncDrizzleDB, user } from "@cashsync/powersync";

import {
  type ExchangeRateRow,
  type ExchangeRatesMap,
  getExchangeRatesQuery,
  getUserExchangeRatesQuery,
  queryKeys,
  ratesMapFromRows,
  type UserExchangeRateRow,
  type UserRatesByDate,
  userRatesByDateFromRows,
} from "../index";

import {
  createPowerSyncQuery,
  type CreatePowerSyncQueryResult,
  DEFAULT_THROTTLE_MS,
} from "./create-power-sync-query";

interface BaseFinanceQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

// ─── Exchange rates ──────────────────────────────────────────────────────────

/**
 * Global rate catalog (`exchange_rate` is a table without user_id that
 * syncs to all clients); `userId` only controls `enabled`.
 */
export function useExchangeRatesQuery(options: BaseFinanceQueryOptions) {
  return createPowerSyncQuery<ExchangeRateRow>({
    queryKey: () => queryKeys.exchangeRates.all,
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      return db ? getExchangeRatesQuery(db) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/**
 * Convenience variant: reactive ISO code → rate vs USD (string) map,
 * the shape consumed by `getRate`/`materializeBaseAmount`.
 */
export function useExchangeRatesMap(options: BaseFinanceQueryOptions): {
  query: ReturnType<typeof useExchangeRatesQuery>;
  ratesMap: Accessor<ExchangeRatesMap>;
} {
  const query = useExchangeRatesQuery(options);
  const ratesMap = createMemo<ExchangeRatesMap>(() => ratesMapFromRows(query.data));
  return { query, ratesMap };
}

// ─── User rates by date (editable, offline) ──────────────────────────────────

/**
 * Rate catalog manually set by user per date (table
 * `user_exchange_rate`, synced per user). Editable offline.
 */
export function useUserExchangeRatesQuery(options: BaseFinanceQueryOptions) {
  return createPowerSyncQuery<UserExchangeRateRow>({
    queryKey: () => queryKeys.exchangeRates.user(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getUserExchangeRatesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/**
 * Convenience variant: reactive `from>base → points by date` index,
 * the shape consumed by `userRateForDate`.
 */
export function useUserRatesByDate(options: BaseFinanceQueryOptions): {
  query: ReturnType<typeof useUserExchangeRatesQuery>;
  ratesByDate: Accessor<UserRatesByDate>;
} {
  const query = useUserExchangeRatesQuery(options);
  const ratesByDate = createMemo<UserRatesByDate>(() => userRatesByDateFromRows(query.data));
  return { query, ratesByDate };
}

// ─── User financial settings ──────────────────────────────────────────────────

export interface FinanceSettingsRow {
  base_currency: string | null;
  month_start_day: number | null;
  role: string | null;
}

/** Default base currency when user has not configured it yet. */
export const DEFAULT_BASE_CURRENCY = "USD";
/** Default accounting month start day. */
export const DEFAULT_MONTH_START_DAY = 1;
/** Maximum allowed for month_start_day (1..28, see period.ts). */
const MONTH_START_DAY_MAX = 28;

export interface UseFinanceSettingsResult {
  /** Underlying query (loading/error states). */
  query: CreatePowerSyncQueryResult<FinanceSettingsRow>;
  /** User base currency (default "USD"). */
  baseCurrency: Accessor<string>;
  /** Accounting month start day, clamped to 1..28 (default 1). */
  monthStartDay: Accessor<number>;
  /** User local role ("admin" enables manual rate refresh). */
  isAdmin: Accessor<boolean>;
}

/**
 * Reactive user financial settings, read from local `user` table
 * (PowerSync keeps them synchronized; write-back already accepts
 * base_currency/month_start_day).
 *
 * Expected usage (connected by orchestrator):
 *   const settings = useFinanceSettings(useFinanceDeps());
 *   accountingMonthRange(new Date(), settings.monthStartDay());
 *   formatMoney(total, settings.baseCurrency(), locale());
 */
export function useFinanceSettings(options: BaseFinanceQueryOptions): UseFinanceSettingsResult {
  const query = createPowerSyncQuery<FinanceSettingsRow>({
    queryKey: () => queryKeys.userPreferences.financeSettings(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      if (!db || !uid) {
        return null;
      }
      return db
        .select({
          base_currency: user.base_currency,
          month_start_day: user.month_start_day,
          role: user.role,
        })
        .from(user)
        .where(eq(user.id, uid))
        .limit(1);
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });

  const row = () => query.data?.[0];
  return {
    query,
    baseCurrency: createMemo(() => row()?.base_currency?.toUpperCase() ?? DEFAULT_BASE_CURRENCY),
    monthStartDay: createMemo(() => {
      const day = row()?.month_start_day;
      return typeof day === "number" && Number.isFinite(day)
        ? Math.min(MONTH_START_DAY_MAX, Math.max(DEFAULT_MONTH_START_DAY, Math.trunc(day)))
        : DEFAULT_MONTH_START_DAY;
    }),
    isAdmin: createMemo(() => row()?.role === "admin"),
  };
}
